import React from 'react';
import { CContainer, CRow, CCol } from '@coreui/react';
import { useResponsive, useResponsiveValue } from 'hooks/useResponsive';
import { ResponsiveValue } from 'utils/responsive';

interface ResponsiveContainerProps {
  children: React.ReactNode;
  className?: string;
  fluid?: boolean;
  maxWidth?: ResponsiveValue<string>;
  minHeight?: ResponsiveValue<string>;
  padding?: ResponsiveValue<string>;
  verticalAlign?: 'top' | 'center' | 'bottom';
  horizontalAlign?: 'left' | 'center' | 'right';
  gap?: ResponsiveValue<string>;
  style?: React.CSSProperties;
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  children,
  className = '',
  fluid = false,
  maxWidth,
  minHeight,
  padding,
  verticalAlign = 'top',
  horizontalAlign = 'left',
  gap,
  style = {}
}) => {
  const { resolveValue } = useResponsive();

  const containerStyles: React.CSSProperties = {
    ...style,
    ...(maxWidth && { maxWidth: resolveValue(maxWidth) }),
    ...(minHeight && { minHeight: resolveValue(minHeight) }),
    ...(padding && { padding: resolveValue(padding) }),
    ...(gap && { gap: resolveValue(gap) }),
    ...(verticalAlign === 'center' && {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }),
    ...(verticalAlign === 'bottom' && {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end'
    }),
    ...(horizontalAlign === 'center' && {
      textAlign: 'center' as const
    }),
    ...(horizontalAlign === 'right' && {
      textAlign: 'right' as const
    })
  };

  return (
    <CContainer
      fluid={fluid}
      className={className}
      style={containerStyles}
    >
      {children}
    </CContainer>
  );
};

interface ResponsiveGridProps {
  children: React.ReactNode;
  columns?: ResponsiveValue<number>;
  gap?: ResponsiveValue<string>;
  verticalAlign?: 'top' | 'center' | 'bottom' | 'stretch';
  className?: string;
  style?: React.CSSProperties;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
  children,
  columns,
  gap,
  verticalAlign = 'top',
  className = '',
  style = {}
}) => {
  const { resolveValue } = useResponsive();

  const getAlignItemsClass = () => {
    switch (verticalAlign) {
      case 'center':
        return 'align-items-center';
      case 'bottom':
        return 'align-items-end';
      case 'stretch':
        return 'align-items-stretch';
      default:
        return 'align-items-start';
    }
  };

  const gridStyles: React.CSSProperties = {
    ...style,
    ...(gap && { gap: resolveValue(gap) })
  };

  const rowClasses = `${getAlignItemsClass()} ${className}`;

  return (
    <CRow className={rowClasses} style={gridStyles}>
      {children}
    </CRow>
  );
};

interface ResponsiveColumnProps {
  children: React.ReactNode;
  size?: ResponsiveValue<number | 'auto'>;
  offset?: ResponsiveValue<number>;
  order?: ResponsiveValue<number>;
  className?: string;
  style?: React.CSSProperties;
}

export const ResponsiveColumn: React.FC<ResponsiveColumnProps> = ({
  children,
  size,
  offset,
  order,
  className = '',
  style = {}
}) => {
  let colClasses = '';
  
  if (size) {
    if (typeof size === 'object') {
      Object.entries(size).forEach(([breakpoint, value]) => {
        const bpSuffix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
        colClasses += ` col${bpSuffix}-${value}`;
      });
    } else {
      colClasses = `col-${size}`;
    }
  } else {
    colClasses = 'col';
  }

  if (offset) {
    if (typeof offset === 'object') {
      Object.entries(offset).forEach(([breakpoint, value]) => {
        const bpSuffix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
        colClasses += ` offset${bpSuffix}-${value}`;
      });
    } else {
      colClasses += ` offset-${offset}`;
    }
  }

  if (order) {
    if (typeof order === 'object') {
      Object.entries(order).forEach(([breakpoint, value]) => {
        const bpSuffix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
        colClasses += ` order${bpSuffix}-${value}`;
      });
    } else {
      colClasses += ` order-${order}`;
    }
  }

  return (
    <CCol className={`${colClasses} ${className}`} style={style}>
      {children}
    </CCol>
  );
};

interface VerticalCenterProps {
  children: React.ReactNode;
  minHeight?: ResponsiveValue<string>;
  className?: string;
  style?: React.CSSProperties;
  method?: 'flexbox' | 'grid';
}

export const VerticalCenter: React.FC<VerticalCenterProps> = ({
  children,
  minHeight = { xs: '60vh', md: '80vh' },
  className = '',
  style = {},
  method = 'flexbox'
}) => {
  const resolvedMinHeight = useResponsiveValue(minHeight);

  const centerStyles: React.CSSProperties = {
    ...style,
    minHeight: resolvedMinHeight,
    ...(method === 'flexbox' && {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }),
    ...(method === 'grid' && {
      display: 'grid',
      placeItems: 'center'
    })
  };

  return (
    <div className={className} style={centerStyles}>
      {children}
    </div>
  );
};

interface FlexBoxProps {
  children: React.ReactNode;
  direction?: ResponsiveValue<'row' | 'column' | 'row-reverse' | 'column-reverse'>;
  justify?: ResponsiveValue<'start' | 'center' | 'end' | 'between' | 'around' | 'evenly'>;
  align?: ResponsiveValue<'start' | 'center' | 'end' | 'stretch' | 'baseline'>;
  wrap?: ResponsiveValue<'nowrap' | 'wrap' | 'wrap-reverse'>;
  gap?: ResponsiveValue<string>;
  className?: string;
  style?: React.CSSProperties;
}

export const FlexBox: React.FC<FlexBoxProps> = ({
  children,
  direction = 'row',
  justify = 'start',
  align = 'start',
  wrap = 'nowrap',
  gap,
  className = '',
  style = {}
}) => {
  const { resolveValue } = useResponsive();

  const flexStyles: React.CSSProperties = {
    ...style,
    display: 'flex',
    flexDirection: resolveValue(direction),
    justifyContent: resolveValue(justify),
    alignItems: resolveValue(align),
    flexWrap: resolveValue(wrap),
    ...(gap && { gap: resolveValue(gap) })
  };

  return (
    <div className={className} style={flexStyles}>
      {children}
    </div>
  );
};

export default ResponsiveContainer;